/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://www.reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, ElementRef, Input, ViewChild, HostListener, Output, OnInit, OnDestroy } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { User } from 'src/app/model';
import { CannedResponse } from 'src/app/modules/manage/model/canned-response/canned-response';
import { CannedResponseType } from 'src/app/modules/manage/model/canned-response/response.type';
import { CannedResponseProvider, ReportNotesProvider } from 'src/app/providers';
import { UserService } from 'src/app/services';
import { ReportNote } from '../../../../../model/report.note';
import { LogService } from 'src/app/services';

@Component({
  selector: 'report-note',
  templateUrl: 'report-note.component.html',
  styleUrls: ['report-note.component.scss']
})
export class ReportNoteComponent implements OnInit, OnDestroy {
  @ViewChild('editor') editor: ElementRef;
  @ViewChild('noteContent') noteContent: ElementRef;

  @Input() note: ReportNote;
  @Input() reportId: string;
  @Input() index: number;
  @Input() length: number;

  @Output() onDeleted: EventEmitter<void> = new EventEmitter();
  @Output() onOrderUp: EventEmitter<void> = new EventEmitter();
  @Output() onOrderDown: EventEmitter<void> = new EventEmitter();

  inEditMode: boolean = false;
  toggleSettings: boolean = false;
  cannedResponses: CannedResponse[] = [];

  currentUser: User;
  private currentUserSub: any;

  isReadOnly: boolean;

  constructor(
    public reportNotesProvider: ReportNotesProvider,
    private cannedResponseProvider: CannedResponseProvider,
    private userService: UserService,
    private logService : LogService
  ) { }

  ngOnInit(): void {
    this.currentUserSub = this.userService.user$.subscribe(currentUser => {
      this.currentUser = currentUser;
      this.isReadOnly = this.note.CREATOR && this.note.CREATOR?.ID != this.currentUser.ID;
    })
    this.loadCannedResponse();
  }

  ngOnDestroy(): void {
    if (this.currentUserSub) this.currentUserSub.unsubscribe();
  }

  @HostListener('mouseleave', ['$event'])
  mouseOutside(event): void {
    if (!this.noteContent.nativeElement.contains(event.target) && this.inEditMode) {
      this.inEditMode = true;
      this.updateNote();
    }
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event): void {
    if (!this.noteContent.nativeElement.contains(event.target) && this.inEditMode) {
      this.inEditMode = false;
      this.updateNote();
    }
  }

  openEditMode(): void {
    setTimeout(() => {
      this.inEditMode = this.note.CREATOR && this.note.CREATOR?.ID == this.currentUser.ID;

      setTimeout(() => {
        if (this.editor)
          this.editor.nativeElement.focus();
      }, 100);
    }, 100);
  }

  updateNote(): void {

    this.reportNotesProvider.updateNote(this.reportId, this.note)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(data);
        else this.note.DATE_UPDATED = new Date();
      });

  }

  deleteNote(): void {
    this.reportNotesProvider.deleteNote(this.reportId, this.note.ID)
      .subscribe(data => {
        if (data.success)
          this.onDeleted.emit();
      });
  }

  clickUp(): void {
    this.onOrderUp.emit();
  }

  clickDown(): void {
    this.onOrderDown.emit();
  }

  appendCannedAction(action: string): void {
    let newLine = false;
    if (this.note.CONTENT) {
      if (this.note.CONTENT.length > 0)
        newLine = true;
    } else this.note.CONTENT = '';

    if (action)
      this.note.CONTENT += (newLine ? ' ' : '') + `${action}`;
  }

  private loadCannedResponse(): void {
    this.cannedResponseProvider.getCannedResponses(1, null, CannedResponseType.NOTES)
      .subscribe(data => {
        if (data.success) {
          this.cannedResponses = CannedResponse.parseArray(data.result.items);
        }
      })
  }
}
